import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Power, Pause, Play, Shield, AlertCircle } from 'lucide-react';
import { AdminNavigation } from '../../../components/Beehive/AdminNavigation.js';

const EmergencyPage: React.FC = () => {
  const [logs, setLogs] = useState<string[]>([]);
  const [queueStatus, setQueueStatus] = useState<'running' | 'paused'>('running');
  const [showConfirmation, setShowConfirmation] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCriticalLogs = async () => {
      try {
        const response = await fetch('/api/admin/logs?level=error');
        if (response.ok) {
          const data = await response.json();
          setLogs(data.logs || []);
        } else {
          setLogs([
            `[${new Date().toISOString()}] ERROR: Agent beehive-fast-02 connection timeout`,
            `[${new Date().toISOString()}] WARN: High memory usage detected on beehive-reasoning-01`,
            `[${new Date().toISOString()}] ERROR: Failed to process task queue item #1234`,
            `[${new Date().toISOString()}] INFO: System recovery initiated`,
            `[${new Date().toISOString()}] WARN: Disk space running low on /var/log`
          ]);
        }
      } catch (error) {
        console.error('Failed to fetch critical logs:', error);
        setLogs([`Error fetching critical logs: ${error}`]);
      }
    };

    fetchCriticalLogs();
    const interval = setInterval(fetchCriticalLogs, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleEmergencyAction = async (action: 'shutdown-all' | 'pause-queue' | 'resume-queue') => {
    setLoading(true);
    try {
      let endpoint = '';
      switch (action) {
        case 'shutdown-all':
          endpoint = '/api/admin/agents/shutdown-all';
          break;
        case 'pause-queue':
          endpoint = '/api/admin/queue/pause';
          break;
        case 'resume-queue':
          endpoint = '/api/admin/queue/resume';
          break;
      }

      const response = await fetch(endpoint, { method: 'POST' });
      
      if (response.ok) {
        if (action === 'pause-queue') {
          setQueueStatus('paused');
        } else if (action === 'resume-queue') {
          setQueueStatus('running');
        }
        
        const timestamp = new Date().toISOString();
        const actionMessage = {
          'shutdown-all': 'All agents shutdown initiated',
          'pause-queue': 'Task queue paused',
          'resume-queue': 'Task queue resumed'
        }[action];
        
        setLogs(prev => [`[${timestamp}] ADMIN: ${actionMessage}`, ...prev]);
      } else {
        console.error(`Failed to execute ${action}`);
        setLogs(prev => [`[${new Date().toISOString()}] ERROR: Failed to execute ${action}`, ...prev]);
      }
    } catch (error) {
      console.error(`Error executing ${action}:`, error);
      setLogs(prev => [`[${new Date().toISOString()}] ERROR: ${action} failed - ${error}`, ...prev]);
    } finally {
      setLoading(false);
      setShowConfirmation(null);
    }
  };

  const EmergencyButton: React.FC<{
    title: string;
    description: string;
    icon: React.ElementType;
    color: 'red' | 'yellow' | 'green';
    action: string;
    onClick: () => void;
    disabled?: boolean;
  }> = ({ title, description, icon: Icon, color, onClick, disabled = false }) => {
    const colorClasses = {
      red: 'from-red-500/20 to-red-600/20 border-red-500/50 hover:from-red-500/30 hover:to-red-600/30',
      yellow: 'from-yellow-500/20 to-yellow-600/20 border-yellow-500/50 hover:from-yellow-500/30 hover:to-yellow-600/30',
      green: 'from-green-500/20 to-green-600/20 border-green-500/50 hover:from-green-500/30 hover:to-green-600/30'
    };

    const iconColors = {
      red: 'text-red-400',
      yellow: 'text-yellow-400',
      green: 'text-green-400'
    };

    return (
      <button
        onClick={onClick}
        disabled={disabled || loading}
        className={`
          glass-dark bg-gradient-to-br ${colorClasses[color]} backdrop-blur-xl border rounded-lg p-6 
          transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed
          disabled:hover:scale-100
        `}
      >
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-lg bg-slate-800/50 ${iconColors[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
            <p className="text-sm text-slate-400">{description}</p>
          </div>
        </div>
      </button>
    );
  };

  const ConfirmationModal: React.FC<{
    action: string;
    onConfirm: () => void;
    onCancel: () => void;
  }> = ({ action, onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-dark bg-gradient-to-br from-slate-900/90 to-slate-800/80 backdrop-blur-xl border border-slate-700/50 rounded-lg p-6 max-w-md mx-4"
      >
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-red-400" />
          <h3 className="text-lg font-semibold text-white">Confirm Emergency Action</h3>
        </div>
        
        <p className="text-slate-300 mb-6">
          Are you sure you want to execute: <strong>{action}</strong>?
          This action cannot be undone and may affect system operations.
        </p>
        
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Confirm
          </button>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="flex flex-col h-full p-6 space-y-6">
      <AdminNavigation />
      
      <div className="flex items-center gap-3">
        <div className="p-2 bg-red-500/20 rounded-lg">
          <Shield className="w-6 h-6 text-red-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white text-drop-shadow">Emergency Controls</h1>
          <p className="text-slate-400">Critical system controls and monitoring</p>
        </div>
      </div>

      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="font-medium text-red-400">Warning</span>
        </div>
        <p className="text-red-300/80 text-sm">
          These controls can significantly impact system operations. Use with extreme caution and only during emergencies.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <EmergencyButton
          title="Shutdown All Agents"
          description="Immediately stop all running agents"
          icon={Power}
          color="red"
          action="Shutdown All Agents"
          onClick={() => setShowConfirmation('shutdown-all')}
        />
        
        <EmergencyButton
          title={queueStatus === 'running' ? 'Pause Task Queue' : 'Resume Task Queue'}
          description={queueStatus === 'running' ? 'Pause processing of new tasks' : 'Resume task processing'}
          icon={queueStatus === 'running' ? Pause : Play}
          color={queueStatus === 'running' ? 'yellow' : 'green'}
          action={queueStatus === 'running' ? 'Pause Task Queue' : 'Resume Task Queue'}
          onClick={() => setShowConfirmation(queueStatus === 'running' ? 'pause-queue' : 'resume-queue')}
        />
      </div>

      <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-lg flex-1">
        <div className="p-4 border-b border-slate-700/50">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            Critical System Logs
          </h3>
          <p className="text-sm text-slate-400 mt-1">Real-time monitoring of system errors and warnings</p>
        </div>
        
        <div className="p-4">
          <div className="bg-slate-900/50 rounded-lg p-4 h-96 overflow-y-auto font-mono text-sm">
            {logs.length === 0 ? (
              <div className="text-slate-500 italic">No critical logs available</div>
            ) : (
              logs.map((log, index) => {
                const isError = log.includes('ERROR');
                const isWarn = log.includes('WARN');
                const isAdmin = log.includes('ADMIN');
                
                let textColor = 'text-slate-300';
                if (isError) textColor = 'text-red-400';
                else if (isWarn) textColor = 'text-yellow-400';
                else if (isAdmin) textColor = 'text-blue-400';
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`${textColor} mb-1 leading-relaxed`}
                  >
                    {log}
                  </motion.div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {showConfirmation && (
        <ConfirmationModal
          action={showConfirmation}
          onConfirm={() => handleEmergencyAction(showConfirmation as 'shutdown-all' | 'pause-queue' | 'resume-queue')}
          onCancel={() => setShowConfirmation(null)}
        />
      )}
    </div>
  );
};

export default EmergencyPage;
